import React, { useState, useEffect } from 'react';
import { View, StyleSheet, ActivityIndicator, Platform } from 'react-native';
import { Image } from 'expo-image';
import { Surface } from 'react-native-paper';

interface LoadingImageProps {
  source: any;
  style: any;
  contentFit?: 'contain' | 'cover' | 'fill' | 'none' | 'scale-down';
  transition?: number;
  cachePolicy?: 'memory' | 'disk' | 'memory-disk' | 'none';
  placeholder?: string;
  onLoad?: () => void;
  onError?: () => void;
}

const LoadingImage: React.FC<LoadingImageProps> = ({
  source,
  style,
  contentFit = 'contain',
  transition = 200,
  cachePolicy = 'memory-disk',
  placeholder,
  onLoad,
  onError,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  const handleLoad = () => {
    setIsLoading(false);
    onLoad?.();
  };

  const handleError = () => {
    setIsLoading(false);
    setHasError(true);
    onError?.();
  };

  // Reset states when source changes
  useEffect(() => {
    setIsLoading(true);
    setHasError(false);
  }, [source]);

  // For web, use a simpler approach without loading states
  if (Platform.OS === 'web') {
    return (
      <Image
        source={source}
        style={style}
        contentFit={contentFit}
        transition={transition}
        cachePolicy={cachePolicy}
        placeholder={placeholder}
        onLoad={handleLoad}
        onError={handleError}
        priority="high"
      />
    );
  }

  // For mobile, keep the loading states
  return (
    <View style={[styles.container, style]}>
      {/* Loading skeleton - only show if actually loading */}
      {isLoading && (
        <Surface style={[styles.skeleton, style]} elevation={1}>
          <ActivityIndicator size="small" color="#e0e0e0" />
        </Surface>
      )}
      
      {/* Error state */}
      {hasError && (
        <Surface style={[styles.errorContainer, style]} elevation={1}>
          <View style={styles.errorContent}>
            <ActivityIndicator size="small" color="#ff6b6b" />
          </View>
        </Surface>
      )}
      
      {/* Actual image */}
      <Image
        source={source}
        style={[
          styles.image,
          style,
          (isLoading || hasError) && styles.hidden
        ]}
        contentFit={contentFit}
        transition={transition}
        cachePolicy={cachePolicy}
        placeholder={placeholder}
        onLoad={handleLoad}
        onError={handleError}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    position: 'relative',
  },
  image: {
    width: '100%',
    height: '100%',
  },
  hidden: {
    opacity: 0,
  },
  skeleton: {
    backgroundColor: '#f5f5f5',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  errorContainer: {
    backgroundColor: '#fff5f5',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  errorContent: {
    justifyContent: 'center',
    alignItems: 'center',
  },
});

export default LoadingImage; 